/**
 * "Change try-on photo" — opened from the try-on wait overlay (TryOnProgress)
 * so a user who can't find this in Profile doesn't have to leave the card
 * that's mid-render to fix it. The job already running was posted with just
 * `{ mira_sku }` (see useTryOnJob.js) — the backend derives the photo from
 * the profile at job-start time, so nothing here can touch it either way.
 * Only the *next* try-on picks up whatever gets saved.
 *
 * Portaled to document.body: TryOnProgress renders inside a swipe card that
 * is `position: absolute` with its own inline z-index (the card stack order
 * in ProductSwipeCard.jsx), which creates a stacking context of its own. A
 * `fixed` sheet left nested in there is trapped inside it — its z-index only
 * gets compared against other things inside that one card, so a root-level
 * sibling like the "Ask Mahi" button was painting over it regardless of how
 * high this sheet's own z-index went. Portaling escapes that trap the same
 * way it would with any modal nested under a transformed/positioned ancestor.
 */
import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Camera, Check, Loader2 } from 'lucide-react';
import { useMahiAuth } from '../../auth';
import useProfilePhotoUpload from '../../hooks/useProfilePhotoUpload';

export default function TryOnPhotoSheet({ onClose }) {
  const { user, refresh } = useMahiAuth();
  const { upload, busy } = useProfilePhotoUpload();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [saved, setSaved] = useState(false);

  const pickFile = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    try {
      await upload(file);
      await refresh();
      setSaved(true);
      setTimeout(onClose, 1500);
    } catch {
      setPreview(null);
    }
  };

  return createPortal(
    <motion.div
      className="fixed inset-0 z-50 flex items-end justify-center"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      data-testid="tryon-photo-sheet"
    >
      <button type="button" aria-label="Close" onClick={onClose}
        className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]" />
      <motion.div
        initial={{ y: 40 }} animate={{ y: 0 }} exit={{ y: 40 }}
        transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-lg bg-white rounded-t-3xl px-5 pt-5 pb-7"
      >
        <div className="w-9 h-1 rounded-full bg-slate-200 mx-auto mb-4" />
        <h3 className="font-mahi-serif text-xl text-slate-900">Change try-on photo</h3>
        <p className="text-[13px] text-slate-500 font-mahi-sans mt-0.5 mb-5">
          Used from your next try-on onward - this one keeps going.
        </p>

        <div className="flex items-center gap-4">
          <div className="relative w-20 h-20 rounded-full bg-slate-100 ring-1 ring-slate-200 overflow-hidden flex items-center justify-center shrink-0">
            {(preview || user?.picture)
              ? <img src={preview || user?.picture} alt="" className="w-full h-full object-cover" />
              : <span className="text-2xl font-semibold text-slate-400 font-mahi-sans">?</span>}
            {busy && (
              <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              </div>
            )}
            {saved && !busy && (
              <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center">
                <Check className="w-6 h-6 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="tryon-photo-input" />
            <button type="button" onClick={pickFile} disabled={busy}
              data-testid="tryon-photo-change"
              className="px-3.5 py-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold font-mahi-sans active:scale-95 transition-transform disabled:opacity-60 inline-flex items-center gap-1.5">
              <Camera className="w-3.5 h-3.5" />
              {saved ? 'Saved' : 'Change photo'}
            </button>
            <p className="text-[11px] text-slate-400 font-mahi-sans mt-1.5">
              Full body, just you, clear background, good light.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}
